import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { SlidersHorizontal, Star } from "lucide-react";
import type { ContactSort } from "@hub/shared";
import { Button, Checkbox, Input, Select, Switch, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { ADDED_OPTIONS, HAS_TARGETS, SORTS, TIERS, WHEN_OPTIONS, emptyFilter, filterCount, filterGrammar, sourceName, type PeopleFilter, type Tri } from "./filter";
import s from "./filter.module.css";

/** Group id to name, for the grammar: `group:"Investors"` rather than an id. */
export function useGroupNames(): Record<string, string> {
  const groups = useEngineQuery("listGroups", undefined);
  return useMemo(() => Object.fromEntries((groups.data ?? []).map((g) => [g.id, g.name])), [groups.data]);
}

/**
 * The phone's filter sheet as a Mac popover: the sliders button with a count dot, and under it the same sections,
 * options and wording as FilterSheet.swift. Every change applies at once; the header says how many people match.
 */
export function FilterPanel({ filter, onChange, count, archivedFixed = false, sorts = SORTS }: { filter: PeopleFilter; onChange: (f: PeopleFilter) => void; count: number; archivedFixed?: boolean; sorts?: { id: ContactSort; label: string }[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // The panel is portalled to the body and placed under the button, so the list's own rows never paint over it.
  const [place, setPlace] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const active = filterCount(filter) - (archivedFixed && filter.archived ? 1 : 0);

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const r = ref.current?.getBoundingClientRect();
      if (r) setPlace({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div ref={ref} className={s.anchor}>
      <Button variant={active > 0 ? "primary" : "outline"} icon={<SlidersHorizontal size={14} />} onClick={() => setOpen((o) => !o)} aria-label={active > 0 ? `Filter, ${active} active` : "Filter"} aria-expanded={open} data-testid="filter-button">
        Filter
      </Button>
      {active > 0 && <span className={s.dot} aria-hidden="true">{active}</span>}
      {open && createPortal(<FilterBody panelRef={panelRef} place={place} filter={filter} onChange={onChange} count={count} archivedFixed={archivedFixed} sorts={sorts} onClose={() => setOpen(false)} />, document.body)}
    </div>
  );
}

function FilterBody({ panelRef, place, filter, onChange, count, archivedFixed, sorts, onClose }: { panelRef: RefObject<HTMLDivElement>; place: { top: number; right: number }; filter: PeopleFilter; onChange: (f: PeopleFilter) => void; count: number; archivedFixed: boolean; sorts: { id: ContactSort; label: string }[]; onClose: () => void }) {
  const navigate = useNavigate();
  const groups = useEngineQuery("listGroups", undefined);
  const sources = useEngineQuery("getContactSources", undefined);
  const fields = useEngineQuery("getCustomFieldDefinitions", undefined);
  const pins = useEngineQuery("getMapPins", {}, { enabled: !filter.location, staleTime: 60_000 });
  const createGroup = useEngineMutation("createGroup", { invalidate: ["listGroups"] });
  const groupNames = useGroupNames();
  const [saveName, setSaveName] = useState("");

  const cities = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of pins.data ?? []) if (p.city) counts.set(p.city, (counts.get(p.city) ?? 0) + 1);
    return [...counts].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [pins.data]);

  const set = (patch: Partial<PeopleFilter>) => onChange({ ...filter, ...patch });
  const toggleIn = <T,>(list: T[], item: T, on: boolean): T[] => (on ? (list.includes(item) ? list : [...list, item]) : list.filter((x) => x !== item));
  const grammar = filterGrammar(filter, groupNames);
  const isEmpty = filterCount(filter) === 0;
  const reset = () => onChange({ ...emptyFilter(filter.sort), archived: archivedFixed });

  const saveGroup = () => {
    const name = saveName.trim();
    if (!name) return;
    createGroup.mutate({ name, kind: "smart", filter: grammar }, {
      onSuccess: (g) => {
        toast(`Saved ${g.name} as a smart group`, "success");
        setSaveName("");
        onClose();
        navigate(`/group/${g.id}`);
      },
    });
  };

  return (
    <div ref={panelRef} className={s.panel} style={{ top: place.top, right: place.right, maxHeight: `calc(100vh - ${place.top + 12}px)` }} role="dialog" aria-label="Filter" data-testid="filter-panel">
      <div className={s.panelHeader}>
        <span className={s.panelTitle}>Filter</span>
        <span className="u-small u-muted">{count.toLocaleString()} {count === 1 ? "person" : "people"}</span>
        <span className="u-grow" />
        <Button size="sm" variant="ghost" onClick={reset} disabled={isEmpty || (archivedFixed && filterCount(filter) === 1 && filter.archived)}>Reset</Button>
        <Button size="sm" variant="primary" onClick={onClose}>Done</Button>
      </div>
      <div className={s.panelBody}>
        {sorts.length > 0 && (
          <div className={s.section}>
            <div className={s.row}>
              <label htmlFor="filter-sort">Sort</label>
              <Select id="filter-sort" value={filter.sort} onChange={(e) => set({ sort: e.target.value as ContactSort })} aria-label="Sort">
                {sorts.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </Select>
            </div>
          </div>
        )}
        {(groups.data ?? []).length > 0 && (
          <div className={s.section}>
            <div className={s.sectionTitle}>Group</div>
            {(groups.data ?? []).map((g) => (
              <div key={g.id} className={s.row}>
                <label>
                  <Checkbox checked={filter.groupIds.includes(g.id)} onChange={(e) => set({ groupIds: toggleIn(filter.groupIds, g.id, e.target.checked) })} />
                  {g.isInternal ? <Star size={12} style={{ color: "#eab308", flex: "none" }} fill="#eab308" /> : <span className={s.groupDot} style={{ background: g.color ?? "var(--accent)" }} />}
                  <span className="u-truncate">{g.name}</span>
                  <span className={s.count}>{g.memberCount}</span>
                </label>
              </div>
            ))}
          </div>
        )}
        <div className={s.section}>
          <div className={s.sectionTitle}>Tier</div>
          {TIERS.map((t) => (
            <div key={t.tier} className={s.row}>
              <label>
                <Checkbox checked={filter.tiers.includes(t.tier)} onChange={(e) => set({ tiers: toggleIn(filter.tiers, t.tier, e.target.checked) })} />
                <span className={s.tierDot} style={{ background: t.color ?? "transparent" }} />
                {t.label}
              </label>
            </div>
          ))}
        </div>
        {(sources.data ?? []).length > 0 && (
          <div className={s.section}>
            <div className={s.sectionTitle}>Source</div>
            {(sources.data ?? []).map((src) => (
              <div key={src.source} className={s.row}>
                <label>
                  <Checkbox checked={filter.sources.includes(src.source)} onChange={(e) => set({ sources: toggleIn(filter.sources, src.source, e.target.checked) })} />
                  <span className="u-truncate">{sourceName(src.source)}</span>
                  <span className={s.count}>{src.count}</span>
                </label>
              </div>
            ))}
          </div>
        )}
        <div className={s.section}>
          <div className={s.sectionTitle}>Last in touch</div>
          {([["lastMet", "Last met"], ["lastEmail", "Last email"], ["lastText", "Last text"]] as const).map(([key, label]) => (
            <div key={key} className={s.row}>
              <label htmlFor={`filter-${key}`}>{label}</label>
              <Select id={`filter-${key}`} value={filter[key]} onChange={(e) => set({ [key]: e.target.value } as Partial<PeopleFilter>)} aria-label={label}>
                {WHEN_OPTIONS.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
              </Select>
            </div>
          ))}
        </div>
        <div className={s.section}>
          <div className={s.sectionTitle}>Has</div>
          {HAS_TARGETS.map(([target, label]) => (
            <div key={target} className={s.row}>
              <label htmlFor={`filter-has-${target}`}>{label}</label>
              <Select id={`filter-has-${target}`} value={filter.has[target] ?? "any"} onChange={(e) => { const has = { ...filter.has }; if (e.target.value === "any") delete has[target]; else has[target] = e.target.value as Tri; set({ has }); }} aria-label={label}>
                <option value="any">Either</option>
                <option value="has">Has</option>
                <option value="missing">Missing</option>
              </Select>
            </div>
          ))}
        </div>
        <div className={s.section}>
          <div className={s.sectionTitle}>Where and what</div>
          <div className={s.row}><Input type="text" placeholder="Location" value={filter.location} onChange={(e) => set({ location: e.target.value })} aria-label="Location" /></div>
          {!filter.location && cities.length > 0 && (
            <div className={s.cities}>
              {cities.map(([city, n]) => (
                <button key={city} type="button" className={s.chip} style={{ cursor: "pointer", border: "none", color: "var(--accent)", background: "var(--accent-soft)" }} onClick={() => set({ location: city })}>{city} · {n}</button>
              ))}
            </div>
          )}
          <div className={s.row}><Input type="text" placeholder="Company" value={filter.company} onChange={(e) => set({ company: e.target.value })} aria-label="Company" /></div>
          <div className={s.row}><Input type="text" placeholder="Title" value={filter.title} onChange={(e) => set({ title: e.target.value })} aria-label="Title" /></div>
          <div className={s.row}><Input type="text" placeholder="Interest" value={filter.interest} onChange={(e) => set({ interest: e.target.value })} aria-label="Interest" /></div>
          {(fields.data ?? []).map((f) => (
            <div key={f.fieldName} className={s.row}>
              <Input type="text" placeholder={f.displayName} value={filter.custom[f.fieldName] ?? ""} onChange={(e) => { const custom = { ...filter.custom }; if (e.target.value) custom[f.fieldName] = e.target.value; else delete custom[f.fieldName]; set({ custom }); }} aria-label={f.displayName} />
            </div>
          ))}
        </div>
        <div className={s.section}>
          <div className={s.row}><span className="u-grow">On Hub</span><Switch on={filter.onHub} onChange={(on) => set({ onHub: on })} label="On Hub" /></div>
          <div className={s.row}>
            <label htmlFor="filter-added">Added</label>
            <Select id="filter-added" value={filter.added} onChange={(e) => set({ added: e.target.value as PeopleFilter["added"] })} aria-label="Added">
              {ADDED_OPTIONS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </Select>
          </div>
          {!archivedFixed && <div className={s.row}><span className="u-grow">Archived</span><Switch on={filter.archived} onChange={(on) => set({ archived: on })} label="Archived" /></div>}
          <div className={s.row}><span className="u-grow">Private</span><Switch on={filter.isPrivate} onChange={(on) => set({ isPrivate: on })} label="Private" /></div>
          <div className={s.row}><span className="u-grow">Memorialized</span><Switch on={filter.memorialized} onChange={(on) => set({ memorialized: on })} label="Memorialized" /></div>
        </div>
        {!isEmpty && (
          <div className={s.section}>
            <div className={s.sectionTitle}>As Hub reads it</div>
            <div className={s.grammar} data-testid="filter-grammar">{grammar}</div>
            <div className={s.row}>
              <Input type="text" placeholder="Group name" value={saveName} onChange={(e) => setSaveName(e.target.value)} aria-label="Group name" onKeyDown={(e) => e.key === "Enter" && saveGroup()} />
              <Button size="sm" onClick={saveGroup} disabled={!saveName.trim() || createGroup.isPending}>Save as group</Button>
            </div>
            <div className={s.footnote}>A saved group is a smart group: it keeps up as people change.</div>
          </div>
        )}
      </div>
    </div>
  );
}
